<template>
  <v-container fluid class="kanban-page pa-4">
    <!-- Header -->
    <div class="kanban-header">
      <div class="d-flex align-center gap-3">
        <v-btn variant="text" size="small" prepend-icon="mdi-arrow-left" to="/admin" class="mr-1">Back</v-btn>
        <v-divider vertical class="mx-1" style="height: 20px;" />
        <v-icon color="primary" size="28">mdi-view-column-outline</v-icon>
        <div>
          <h1 class="text-h6 font-weight-bold mb-0">Development Board</h1>
          <p class="text-caption text-medium-emphasis mb-0">{{ totalCards }} cards across {{ COLUMNS.length }} columns</p>
        </div>
      </div>
      <div class="d-flex gap-2">
        <v-btn variant="tonal" color="primary" prepend-icon="mdi-plus" size="small" @click="openCreate()">New Card</v-btn>
        <v-btn variant="text" icon="mdi-refresh" size="small" :loading="store.loading" @click="store.fetchBoard()" />
      </div>
    </div>

    <!-- Board -->
    <div class="kanban-outer">
      <div class="kanban-board">
        <div
          v-for="col in COLUMNS" :key="col.key" class="kanban-column" :data-column="col.key"
          @dragover.prevent="onDragOver($event, col.key)" @drop="onDrop($event, col.key)" @dragleave="onDragLeave"
        >
          <div class="column-header" :style="{ borderTopColor: col.color }">
            <div class="d-flex align-center gap-2">
              <v-icon :color="col.color" size="16">{{ col.icon }}</v-icon>
              <span class="column-title">{{ col.label }}</span>
              <v-chip size="x-small" variant="tonal" :color="col.color">{{ store.columns[col.key]?.length || 0 }}</v-chip>
            </div>
            <v-btn icon="mdi-plus" size="x-small" variant="text" :color="col.color" @click="openCreate(col.key)" />
          </div>
          <div class="column-cards" :class="{ 'drag-over': dragOverColumn === col.key }">
            <div
              v-for="card in store.columns[col.key]" :key="card.cardId"
              class="kanban-card" draggable="true"
              @dragstart="onDragStart($event, card)" @dragend="onDragEnd" @click="openCard(card)"
            >
              <div class="priority-stripe" :style="{ backgroundColor: priorityColor(card.priority) }" />
              <div class="card-body">
                <div v-if="card.tags" class="mb-1 d-flex flex-wrap gap-1">
                  <v-chip v-for="tag in card.tags.split(',')" :key="tag" size="x-small" variant="tonal" color="primary">{{ tag.trim() }}</v-chip>
                </div>
                <div class="card-title">{{ card.title }}</div>
                <div v-if="card.detail" class="card-detail">{{ card.detail }}</div>
                <div class="card-footer mt-2">
                  <div class="d-flex align-center gap-1">
                    <v-icon size="12" color="grey">mdi-pound</v-icon>
                    <span class="text-caption text-medium-emphasis">{{ card.cardId }}</span>
                    <v-tooltip v-if="card.source === 'contact_form'" text="From contact form">
                      <template #activator="{ props }">
                        <v-icon v-bind="props" size="12" color="purple">mdi-account-arrow-right</v-icon>
                      </template>
                    </v-tooltip>
                  </div>
                  <div class="d-flex align-center gap-1">
                    <v-tooltip v-if="card.dueDate" :text="'Due: ' + formatDate(card.dueDate)">
                      <template #activator="{ props }">
                        <v-icon v-bind="props" size="12" :color="isOverdue(card.dueDate) ? 'error' : 'grey'">mdi-calendar</v-icon>
                      </template>
                    </v-tooltip>
                    <v-tooltip v-if="card.assignedName" :text="card.assignedName">
                      <template #activator="{ props }">
                        <v-avatar v-bind="props" size="20" color="primary" class="text-caption">{{ initials(card.assignedName) }}</v-avatar>
                      </template>
                    </v-tooltip>
                  </div>
                </div>
              </div>
            </div>
            <div v-if="!store.columns[col.key]?.length" class="column-empty">
              <v-icon size="24" color="grey-lighten-1">{{ col.icon }}</v-icon>
              <span class="text-caption text-disabled mt-1">Empty</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Card Detail Dialog -->
    <v-dialog v-model="cardDialog" max-width="720" scrollable>
      <v-card v-if="store.activeCard" rounded="lg">
        <v-card-title class="d-flex align-center pa-4 pb-2">
          <v-icon class="mr-2" :color="columnColor(store.activeCard.column)">{{ columnIcon(store.activeCard.column) }}</v-icon>
          <span class="text-body-1 font-weight-bold flex-grow-1">{{ editMode ? 'Edit Card' : store.activeCard.title }}</span>
          <v-chip size="small" variant="tonal" :color="priorityColor(store.activeCard.priority)" class="mr-2">{{ store.activeCard.priority }}</v-chip>
          <v-btn icon="mdi-pencil" size="small" variant="text" @click="editMode = !editMode" />
          <v-btn icon="mdi-close" size="small" variant="text" @click="closeCard" />
        </v-card-title>
        <v-divider />
        <v-card-text class="pa-4">
          <!-- Edit mode -->
          <template v-if="editMode">
            <v-text-field v-model="editForm.title" label="Title" variant="outlined" density="compact" class="mb-3" />
            <v-textarea v-model="editForm.detail" label="Detail" variant="outlined" density="compact" rows="3" class="mb-3" />
            <v-row dense>
              <v-col cols="6"><v-select v-model="editForm.column" :items="COLUMNS.map(c => ({ title: c.label, value: c.key }))" label="Column" variant="outlined" density="compact" /></v-col>
              <v-col cols="6"><v-select v-model="editForm.priority" :items="PRIORITIES.map(p => ({ title: p.label, value: p.value }))" label="Priority" variant="outlined" density="compact" /></v-col>
              <v-col cols="6">
                <v-select v-model="editForm.assignedTo" :items="[{ title: 'Unassigned', value: null }, ...store.teamMembers.map(m => ({ title: m.displayName || m.username, value: m.userId }))]" label="Assign to" variant="outlined" density="compact" />
              </v-col>
              <v-col cols="6"><v-text-field v-model="editForm.dueDate" label="Due date" type="date" variant="outlined" density="compact" /></v-col>
              <v-col cols="12"><v-text-field v-model="editForm.tags" label="Tags (comma separated)" variant="outlined" density="compact" /></v-col>
            </v-row>
            <div class="d-flex gap-2 mt-2">
              <v-btn color="primary" size="small" :loading="saving" @click="saveEdit">Save</v-btn>
              <v-btn variant="text" size="small" @click="editMode = false">Cancel</v-btn>
              <v-spacer />
              <v-btn color="error" size="small" variant="text" prepend-icon="mdi-delete" @click="confirmDelete">Delete</v-btn>
            </div>
          </template>

          <!-- View mode -->
          <template v-else>
            <h2 class="text-h6 font-weight-bold mb-2">{{ store.activeCard.title }}</h2>
            <p v-if="store.activeCard.detail" class="text-body-2 mb-4">{{ store.activeCard.detail }}</p>
            <div class="meta-grid mb-4">
              <div class="meta-item">
                <span class="meta-label">Column</span>
                <v-chip size="small" variant="tonal" :color="columnColor(store.activeCard.column)">{{ columnLabel(store.activeCard.column) }}</v-chip>
              </div>
              <!-- Quick assign — always visible in view mode -->
              <div class="meta-item">
                <span class="meta-label">Assigned to</span>
                <v-select
                  :model-value="store.activeCard.assignedTo"
                  :items="[{ title: 'Unassigned', value: null }, ...store.teamMembers.map(m => ({ title: m.displayName || m.username, value: m.userId }))]"
                  variant="outlined" density="compact" hide-details style="min-width: 160px;"
                  @update:model-value="quickAssign"
                />
              </div>
              <div class="meta-item" v-if="store.activeCard.dueDate">
                <span class="meta-label">Due</span>
                <span class="text-body-2" :class="{ 'text-error': isOverdue(store.activeCard.dueDate) }">{{ formatDate(store.activeCard.dueDate) }}</span>
              </div>
              <div class="meta-item">
                <span class="meta-label">Created</span>
                <span class="text-body-2">{{ formatDate(store.activeCard.createdAt) }}</span>
              </div>
            </div>
            <div class="mb-4">
              <p class="text-caption text-medium-emphasis mb-1">MOVE TO</p>
              <div class="d-flex flex-wrap gap-1">
                <v-btn v-for="col in COLUMNS" :key="col.key" size="x-small" :variant="store.activeCard.column === col.key ? 'flat' : 'outlined'" :color="col.color" @click="quickMove(col.key)">{{ col.label }}</v-btn>
              </div>
            </div>
          </template>

          <v-divider class="my-4" />

          <!-- Comments -->
          <div class="comments-section">
            <p class="text-caption font-weight-bold text-medium-emphasis mb-3">COMMENTS ({{ flatCommentCount }})</p>
            <CommentThread v-for="comment in store.comments" :key="comment.commentId" :comment="comment" @reply="setReplyTo" />
            <div v-if="!store.comments.length" class="text-center text-disabled py-4">
              <v-icon size="32" class="mb-1">mdi-comment-outline</v-icon>
              <p class="text-caption">No comments yet</p>
            </div>
            <div class="mt-3">
              <p v-if="replyTo" class="text-caption text-medium-emphasis mb-1">
                Replying to comment #{{ replyTo }}
                <v-btn size="x-small" variant="text" @click="replyTo = null">Cancel</v-btn>
              </p>
              <v-textarea v-model="newComment" :label="replyTo ? 'Write a reply...' : 'Add a comment...'" variant="outlined" density="compact" rows="2" hide-details class="mb-2" />
              <v-btn size="small" color="primary" variant="tonal" :disabled="!newComment.trim()" :loading="postingComment" @click="submitComment">{{ replyTo ? 'Reply' : 'Comment' }}</v-btn>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Create Card Dialog -->
    <v-dialog v-model="createDialog" max-width="520">
      <v-card rounded="lg">
        <v-card-title class="pa-4 pb-2">
          <v-icon class="mr-2" color="primary">mdi-plus-circle-outline</v-icon>New Card
        </v-card-title>
        <v-divider />
        <v-card-text class="pa-4">
          <v-text-field v-model="createForm.title" label="Title *" variant="outlined" density="compact" class="mb-3" autofocus />
          <v-textarea v-model="createForm.detail" label="Detail" variant="outlined" density="compact" rows="3" class="mb-3" />
          <v-row dense>
            <v-col cols="6"><v-select v-model="createForm.column" :items="COLUMNS.map(c => ({ title: c.label, value: c.key }))" label="Column" variant="outlined" density="compact" /></v-col>
            <v-col cols="6"><v-select v-model="createForm.priority" :items="PRIORITIES.map(p => ({ title: p.label, value: p.value }))" label="Priority" variant="outlined" density="compact" /></v-col>
            <v-col cols="6"><v-select v-model="createForm.assignedTo" :items="[{ title: 'Unassigned', value: null }, ...store.teamMembers.map(m => ({ title: m.displayName || m.username, value: m.userId }))]" label="Assign to" variant="outlined" density="compact" /></v-col>
            <v-col cols="6"><v-text-field v-model="createForm.dueDate" label="Due date" type="date" variant="outlined" density="compact" /></v-col>
            <v-col cols="12"><v-text-field v-model="createForm.tags" label="Tags (comma separated)" variant="outlined" density="compact" /></v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="pa-4 pt-0">
          <v-spacer />
          <v-btn variant="text" @click="createDialog = false">Cancel</v-btn>
          <v-btn color="primary" variant="flat" :loading="saving" :disabled="!createForm.title.trim()" @click="submitCreate">Create</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useKanbanStore, COLUMNS, PRIORITIES } from '../../stores/kanban'
import CommentThread from '../../components/kanban/CommentThread.vue'

const store = useKanbanStore()

const draggingCard = ref(null)
const dragOverColumn = ref(null)

function onDragStart(e, card) { draggingCard.value = card; e.dataTransfer.effectAllowed = 'move'; e.currentTarget.classList.add('dragging') }
function onDragEnd(e) { e.currentTarget.classList.remove('dragging'); draggingCard.value = null; dragOverColumn.value = null }
function onDragOver(e, colKey) { dragOverColumn.value = colKey; e.dataTransfer.dropEffect = 'move' }
function onDragLeave() { dragOverColumn.value = null }
function onDrop(e, colKey) {
  dragOverColumn.value = null
  if (!draggingCard.value) return
  store.moveCard(draggingCard.value.cardId, colKey, store.columns[colKey].length)
  draggingCard.value = null
}

const cardDialog = ref(false)
const editMode = ref(false)
const saving = ref(false)
const editForm = ref({})

function openCard(card) { store.setActiveCard(card); editForm.value = { ...card }; editMode.value = false; cardDialog.value = true }
function closeCard() { cardDialog.value = false; store.setActiveCard(null); editMode.value = false }
async function saveEdit() { saving.value = true; try { await store.updateCard(store.activeCard.cardId, editForm.value); editMode.value = false } finally { saving.value = false } }
async function quickMove(colKey) { await store.updateCard(store.activeCard.cardId, { column: colKey }); cardDialog.value = false }

async function quickAssign(userId) {
  if (!store.activeCard?.cardId) return
  await store.updateCard(store.activeCard.cardId, { assignedTo: userId })
}
async function confirmDelete() { if (!confirm(`Delete card "${store.activeCard.title}"?`)) return; await store.deleteCard(store.activeCard.cardId); closeCard() }

const createDialog = ref(false)
const createForm = ref({ title: '', detail: '', column: 'todo', priority: 'medium', assignedTo: null, dueDate: '', tags: '' })

function openCreate(col = 'todo') { createForm.value = { title: '', detail: '', column: col, priority: 'medium', assignedTo: null, dueDate: '', tags: '' }; createDialog.value = true }
async function submitCreate() { saving.value = true; try { await store.createCard(createForm.value); createDialog.value = false } finally { saving.value = false } }

const newComment = ref('')
const replyTo = ref(null)
const postingComment = ref(false)

function setReplyTo(commentId) { replyTo.value = commentId }
async function submitComment() {
  if (!newComment.value.trim()) return
  postingComment.value = true
  try { await store.addComment(store.activeCard.cardId, newComment.value, replyTo.value); newComment.value = ''; replyTo.value = null }
  finally { postingComment.value = false }
}

const flatCommentCount = computed(() => {
  function count(comments) { return comments.reduce((acc, c) => acc + 1 + count(c.replies || []), 0) }
  return count(store.comments)
})


const totalCards = computed(() => Object.values(store.columns).reduce((a, c) => a + c.length, 0))
function priorityColor(p) { return PRIORITIES.find(x => x.value === p)?.color || '#6B7280' }
function columnColor(key) { return COLUMNS.find(c => c.key === key)?.color || '#6B7280' }
function columnIcon(key) { return COLUMNS.find(c => c.key === key)?.icon || 'mdi-circle' }
function columnLabel(key) { return COLUMNS.find(c => c.key === key)?.label || key }
function initials(name) { return name?.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() || '?' }
function formatDate(d) { if (!d) return ''; return new Date(d).toLocaleDateString('da-DK', { day: 'numeric', month: 'short', year: 'numeric' }) }
function isOverdue(d) { return d && new Date(d) < new Date() }

onMounted(() => { store.fetchBoard(); store.fetchTeam() })
</script>

<style scoped>
.kanban-page { min-height: calc(100vh - 64px); }
.kanban-header { display: flex; align-items: center; justify-content: space-between; max-width: 1600px; margin: 0 auto 20px auto; padding: 0 4px; }
.kanban-outer { display: flex; justify-content: center; width: 100%; }
.kanban-board { display: flex; gap: 12px; overflow-x: auto; padding-bottom: 16px; align-items: flex-start; max-width: 1600px; width: 100%; }
.kanban-column { flex: 1 1 0; min-width: 200px; max-width: 260px; background: rgba(var(--v-theme-surface-variant), 0.4); border-radius: 10px; overflow: hidden; }
.column-header { display: flex; align-items: center; justify-content: space-between; padding: 10px 10px 10px 12px; border-top: 3px solid transparent; background: rgba(var(--v-theme-surface), 0.8); }
.column-title { font-size: 0.78rem; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; }
.column-cards { padding: 8px; min-height: 80px; transition: background 0.15s; }
.column-cards.drag-over { background: rgba(var(--v-theme-primary), 0.06); border-radius: 0 0 10px 10px; }
.kanban-card { background: rgb(var(--v-theme-surface)); border-radius: 8px; margin-bottom: 8px; display: flex; cursor: pointer; border: 1px solid rgba(var(--v-theme-on-surface), 0.08); transition: transform 0.15s, box-shadow 0.15s; overflow: hidden; }
.kanban-card:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(0,0,0,0.15); }
.kanban-card.dragging { opacity: 0.5; transform: rotate(2deg); }
.priority-stripe { width: 3px; flex-shrink: 0; }
.card-body { padding: 10px; flex: 1; min-width: 0; }
.card-title { font-size: 0.82rem; font-weight: 600; line-height: 1.3; margin-bottom: 4px; }
.card-detail { font-size: 0.75rem; color: rgba(var(--v-theme-on-surface), 0.6); display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.card-footer { display: flex; align-items: center; justify-content: space-between; }
.column-empty { display: flex; flex-direction: column; align-items: center; padding: 20px 0; opacity: 0.4; }
.meta-grid { display: flex; flex-wrap: wrap; gap: 16px; align-items: flex-end; }
.meta-item { display: flex; flex-direction: column; gap: 4px; }
.meta-label { font-size: 0.65rem; text-transform: uppercase; letter-spacing: 0.08em; color: rgba(var(--v-theme-on-surface), 0.4); font-weight: 600; }
</style>